import React, { useState, useRef } from 'react';
import Modal from 'react-modal';
import Alert from '@components/Widgets/Alert/Alert.react';
import { getPrimaryMeal, getParticipantsInfo, normalizeParticipantsToArray, getParticipantsForSpecificMeal } from '@utils/Meals';
import { getMealAndLeftovers } from '@utils/proMeals';
import { isSunbasketFood } from '@utils/Sunbasket';
import { calculateTotalServingsForParticipants, getBaseServings, calculateRequiredScaling } from '@utils/servingsCalculations';
import {
    buildProLeftoverSlots,
    isParticipantChecked,
    areAllParticipantsSelected,
    toggleParticipantSelection,
    toggleAllParticipants,
    updateGuestCount,
    getGuestCount,
    findConflictingMeals
} from '@utils/leftoverUtils';
import '../../../../Planner/Modals/LeftoverDatesModal.scss';

const LeftoverOffsetsModal = ({ user, plan, recipes, foods, leftoverParams, ctaTitle = 'Save', closeModal, leftOverMealType, onSelectLeftoverOffsets, rescheduleMealParams, rescheduleMeals, returnMode = false, onReturnLeftoverData, onShowConflictModal }) => {

    const { leftovers, leftoverOffset, meal, participants } = leftoverParams;
    const meals = plan.items;

    // Build available slots starting from next meal slot after main meal
    const availableSlots = buildProLeftoverSlots(meal, plan, rescheduleMealParams);

    const participantDetails = getParticipantsInfo(user);
    const defaultParticipants = participants ?? participantDetails.filter((p) => (meal.participants || []).includes(p.uuid));

    let oldLeftovers = meals.filter((m) => m.parent == meal.id);
    let defaultSelected = oldLeftovers.map((l, index) => {
        const mealWithArrayParticipants = {
            ...l,
            participants: normalizeParticipantsToArray(l.participants)
        };

        return {
            offset: l.offset,
            mealType: l.meal,
            participants: getParticipantsForSpecificMeal(mealWithArrayParticipants, user)
        };
    });

    if (leftovers) {
        oldLeftovers = leftovers;
        defaultSelected = oldLeftovers.map((l, index) => {
            const isAlreadyProcessed = Array.isArray(l.participants) &&
                                      l.participants.length > 0 &&
                                      typeof l.participants[0] === 'object';

            const processedParticipants = isAlreadyProcessed
                ? l.participants
                : getParticipantsForSpecificMeal({ ...l, participants: l.participants }, user);

            return {
                offset: l.leftoverOffset,
                mealType: l.mealType,
                participants: processedParticipants
            };
        });
    }

    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(() => defaultSelected.length && !rescheduleMealParams ? defaultSelected : []);
    const [extraServings, setExtraServings] = useState(0);
    const [expandedSlots, setExpandedSlots] = useState({});
    const initialSelectedRef = useRef(defaultSelected.length && !rescheduleMealParams ? defaultSelected : []);

    const getScalingMap = () => {
        const dishServings  = getTotalServings();
        let scalingMap = {};

        dishServings.forEach(({uuid, scaling}) => {
            scalingMap[uuid] = scaling;
        });

        return scalingMap;
    }

    const getDishes = () => {
        let contents = {};

        const mealDishes = meals.filter(m => m?.meal === meal.meal && m?.offset === meal.offset && !m?.deleted && (recipes[m?.recipe_uuid]?.servings || isSunbasketFood(foods[m?.food_uuid])));

        mealDishes.forEach((dish) => {
            if(recipes[dish.recipe_uuid]?.servings) {
                contents[dish.recipe_uuid] = recipes[dish.recipe_uuid]
            }

            if(foods[dish.food_uuid]?.servings) {
                contents[dish.food_uuid] = foods[dish.food_uuid]
            }
        });

        const dishes= [];

        mealDishes.forEach(mealItem => {
            let dish = Object.assign({}, mealItem);
            if (dish.meal_type !== 'leftover') {
                // Use PRO utility to get meal and its leftovers
                const mealAndLeftovers = getMealAndLeftovers(dish, plan);
                const leftovers = mealAndLeftovers.slice(1); // First item is the main meal, rest are leftovers

                dish = { ...dish, main: mealItem, leftovers: leftovers };
                dishes.push(dish);
            }
        });

        return { dishes, contents };
    }

    const getTotalServings = () => {
        const {dishes, contents} = getDishes();

        return dishes.map(({main: dish}) => {
            const dishContent = contents[dish.recipe_uuid || dish.food_uuid];

            // Get all participants: default (main meal) + selected (leftovers)
            const leftoverParticipants = selected.flatMap(s => s.participants);
            const allParticipants = [...defaultParticipants, ...leftoverParticipants];

            // Use utility to calculate total servings (handles USER logged servings)
            const totalServings = calculateTotalServingsForParticipants(allParticipants, dish, dishContent);

            // Calculate leftover servings separately (only from selected leftovers)
            const totalLeftoverServings = calculateTotalServingsForParticipants(leftoverParticipants, dish, dishContent);

            const baseServings = getBaseServings(dishContent);
            const scaling = calculateRequiredScaling(totalServings + extraServings, baseServings);
            const totalYield = scaling * baseServings;
            const uuid = dish.uuid || dish.id;
            const title = dishContent.title || dishContent.name;

            return {uuid, title, totalYield, totalServings, totalLeftoverServings, scaling};
        });
    }

    const isChecked = (offset, mealTypeName, participantUuid) => {
        return isParticipantChecked(selected, 'offset', offset, mealTypeName, participantUuid);
    }

    const allParticipantsSelected = (offset, mealTypeName) => {
        const match = selected.find(item => item.offset === offset && item.mealType === mealTypeName);
        return areAllParticipantsSelected(match, participantDetails);
    }

    const toggleCheckbox = (offset, mealTypeName, participantUuid) => {
        setSelected(prev => toggleParticipantSelection(prev, 'offset', offset, mealTypeName, participantUuid, participantDetails));
    };

    const selectAllParticipants = (offset, mealTypeName) => {
        setSelected(prev => toggleAllParticipants(prev, 'offset', offset, mealTypeName, participantDetails));
    }

    const onChangeNumberOfGuests = (offset, mealTypeName, newGuestCount) => {
        setSelected(prev => updateGuestCount(prev, 'offset', offset, mealTypeName, newGuestCount, 'Guest #'));
    };

    const getCurrentGuestCount = (offset, mealTypeName) => {
        return getGuestCount(selected, 'offset', offset, mealTypeName);
    };

    const clearAll = () => {
        setSelected([]);
    }

    const toggleSlotExpand = (offset, mealTypeName) => {
        const key = `${offset}_${mealTypeName}`;
        setExpandedSlots(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getSelectedCount = (offset, mealTypeName) => {
        const match = selected.find(item => item.offset === offset && item.mealType === mealTypeName);
        return match ? match.participants.length : 0;
    };

    const onClickCta = () => {
        if(rescheduleMealParams) {
            const userLeftovers = selected.map(({offset, mealType, participants}) => {
                return {leftoverOffset: offset, mealType, participants};
            });
            rescheduleMeals(rescheduleMealParams.meals, rescheduleMealParams.offset, rescheduleMealParams.mealType, userLeftovers);
        } else if (returnMode && onReturnLeftoverData) {
            // Return mode: send data back to parent instead of applying directly
            const leftovers = selected.map(({offset, mealType, participants}) => {
                // Check if this leftover already exists with same offset and mealType
                // If only participants changed, it's not a conflict
                const isExistingLeftover = oldLeftovers.some(oldLeftover => {
                    // Structure 1: from meals.filter (fields: offset, meal)
                    if (oldLeftover.offset !== undefined && oldLeftover.meal) {
                        return oldLeftover.offset === offset && oldLeftover.meal === mealType;
                    }
                    // Structure 2: from leftovers prop (fields: leftoverOffset, mealType)
                    if (oldLeftover.leftoverOffset !== undefined && oldLeftover.mealType) {
                        return oldLeftover.leftoverOffset === offset && oldLeftover.mealType === mealType;
                    }
                    return false;
                });

                // Find conflicting meals only if this is a NEW leftover (offset/mealType changed)
                const conflictingMeals = isExistingLeftover
                    ? []
                    : findConflictingMeals(offset, mealType, [], meals, 'offset', 'meal');

                return {leftoverOffset: offset, mealType, participants, conflictingMeals};
            });
            const scalingMap = getScalingMap();
            const hasConflicts = leftovers.some(leftover => leftover.conflictingMeals.length > 0);

            // Show conflict modal if conflicts exist
            if (hasConflicts && onShowConflictModal) {
                onShowConflictModal(leftovers, scalingMap, extraServings);
            } else {
                onReturnLeftoverData(leftovers, scalingMap, extraServings);
            }
            closeModal();
        } else {
            // Non-return mode (swap meal flow): check for conflicts before applying
            const leftovers = selected.map(({offset, mealType, participants}) => {
                // Note: No need to filter out main meal/existing leftovers since this is for adding/swapping
                const conflictingMeals = findConflictingMeals(offset, mealType, [], meals, 'offset', 'meal');
                return {leftoverOffset: offset, mealType, participants, conflictingMeals};
            });
            const scalingMap = getScalingMap();
            const hasConflicts = leftovers.some(leftover => leftover.conflictingMeals.length > 0);

            if (hasConflicts && onShowConflictModal) {
                onShowConflictModal(leftovers, scalingMap, extraServings);
                closeModal();
            } else {
                onSelectLeftoverOffsets(leftovers, scalingMap, extraServings);
            }
        }
    }

    const dishServings = getTotalServings();

    const { dishes: allDishes, contents: allContents } = getDishes();
    const singleDishTitle =
        leftoverParams.recipe?.title ||
        (allDishes.length === 1 ? (allContents[allDishes[0].main.recipe_uuid]?.title || allContents[allDishes[0].main.food_uuid]?.name || null) : null);

    const isDirty = !returnMode && JSON.stringify(selected) !== JSON.stringify(initialSelectedRef.current);

    const dishLabel = singleDishTitle || 'This meal';
    const contextBannerText = !returnMode
        ? rescheduleMealParams
            ? <><strong>{dishLabel} has leftovers that need to be rescheduled.</strong> Select the meals where you'd like them to go.</>
            : <><strong>{dishLabel} makes extra servings.</strong> Select the meal slots where you'd like to use the leftovers.</>
        : null;

    // Calculate total leftover servings including guests
    const totalLeftoverServings = selected.reduce((sum, selection) => {
        return sum + selection.participants.reduce((participantSum, participant) => {
            return participantSum + participant.portion;
        }, 0);
    }, 0);

    return (
        <Modal isOpen={true}
            onRequestClose={closeModal}
            closeModal={closeModal}
            contentLabel="Overwrite with leftovers"
            className="el-modal el-modal2 el-modal2-centered leftovers-modal leftovers-offset-modal"
            overlayClassName="el-modal-overlay"
            closeTimeoutMS={250}>
            <section className="el-modal-container el-modal2-container leftovers-modal-container">
                <header>
                    <h2>{!returnMode ? 'What should we do with the leftovers?' : 'Leftovers and Batch Cooking'}</h2>
                    {(returnMode || selected.length > 0) && <button className="clear-selections" onClick={() => clearAll()}>Clear all</button>}
                    <button className="el-modal-close-x" onClick={closeModal}>
                        <i className="icon-close-x" />
                        <span className="assistive-text">Close Modal</span>
                    </button>
                </header>

                <div className="el-modal-body-container el-modal2-body-container el-fonts leftovers-modal-body">
                    {error ? <Alert type="error" description={error} /> : null}
                    {contextBannerText && <Alert type="info" iconType="no-icon" description={contextBannerText} />}

                    <div className="leftovers-section-label">Schedule leftovers for a specific meal</div>

                    <div className="leftovers-container">
                        {availableSlots.map((slot, slotIndex) => {
                            const { offset, mealTypes } = slot;
                            const offsetFormat = `Day ${offset + 1}`;

                            return (
                                <div key={`${offset}-${slotIndex}`} className="leftover-date">
                                    <span className="date">
                                        <i className="icon-calendar" />
                                        {offsetFormat}
                                    </span>
                                    {mealTypes.map((mealType) => {
                                        const mealTypeOffsetMeals = meals.filter((m) => m.offset === offset && m.meal === mealType.name);
                                        const { titles } = getPrimaryMeal(mealTypeOffsetMeals, recipes, foods);
                                        const hasScheduledMeal = titles.length > 0;
                                        const slotKey = `${offset}_${mealType.name}`;
                                        const isExpanded = !!expandedSlots[slotKey];
                                        const selectedCount = getSelectedCount(offset, mealType.name);
                                        const isSlotSelected = allParticipantsSelected(offset, mealType.name);

                                        return (
                                            <div key={`${offsetFormat}_${mealType.name}`} className={`meal-card ${isSlotSelected ? 'is-selected' : ''}`}>
                                                <div className="meal-main">
                                                    <button
                                                        className="el-checkbox meal-check"
                                                        data-active={isSlotSelected}
                                                        onClick={() => selectAllParticipants(offset, mealType.name)}
                                                    ></button>
                                                    <div className="meal-info">
                                                        <span className="meal-type-name">{mealType.name}</span>
                                                        {hasScheduledMeal && (
                                                            <span className="meal-dish">
                                                                <span className="scheduled-badge">Scheduled</span>
                                                                <span className="meal-dish-title">{titles.join(' + ')}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {participantDetails.length > 1 && (
                                                        <button
                                                            className="participants-toggle"
                                                            onClick={() => toggleSlotExpand(offset, mealType.name)}
                                                        >
                                                            {selectedCount > 0 && `${selectedCount} ${selectedCount === 1 ? 'person' : 'people'}`}
                                                            <span className={`toggle-arrow ${isExpanded ? 'expanded' : ''}`}>▼</span>
                                                        </button>
                                                    )}
                                                </div>
                                                {participantDetails.length > 1 && isExpanded && (
                                                    <div className="participants-panel">
                                                        {participantDetails.map((participant, participantIndex) => {
                                                            const participantIsChecked = isChecked(offset, mealType.name, participant.uuid);

                                                            return (
                                                                <div key={`${offsetFormat}_${mealType.name}_${participant.name}`} className="participant-container">
                                                                    <button
                                                                        className="el-checkbox"
                                                                        data-active={participantIsChecked}
                                                                        onClick={() => toggleCheckbox(offset, mealType.name, participant.uuid)}
                                                                    >
                                                                        <span className="participant-name">{participant.name}</span>
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}

                                                        {/* Guest controls hidden in PRO mode */}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <footer>
                    <button className="el-modal-cancel-btn" onClick={closeModal}>Cancel</button>
                    <button className="el-modal-ok-btn" onClick={() => onClickCta()}>{!returnMode ? (isDirty ? 'Done' : 'Skip for now') : 'Done'}</button>
                </footer>
            </section>
        </Modal>
    );
}

export default LeftoverOffsetsModal;